import React, { useState } from 'react';
import { Apple, Globe, X } from 'lucide-react';
import { isIOSDevice } from '../../utils/iosInstallContext';

/**
 * Google Play + App Store badge buttons for the marketing site.
 *
 * - Google Play button → opens the live listing.
 * - App Store button:
 *   • On iOS visitors → transforms into an active "Use in Safari" CTA pointing
 *     straight at the auth flow so iPhone users don't think Allure is unavailable.
 *   • On non-iOS visitors → still says "Coming soon to App Store" but is now
 *     CLICKABLE (was disabled) and opens an info modal explaining the iOS app
 *     is coming soon and offering a "Use it now in your browser" CTA. This way
 *     anyone showing the page to an iPhone-using friend, or any iOS user our
 *     UA-sniffer fails to detect, still gets a clear path forward.
 *
 * Used on the marketing home + final CTA + footer.
 */

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.allurexp.app';
const SAFARI_APP_URL = '/login';

const PlayStoreIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3.609 1.814L13.792 12 3.609 22.186a1 1 0 01-.609-.921V2.735a1 1 0 01.609-.921z" fill="#34A853"/>
    <path d="M16.493 8.708L6.4 2.872l9.585 9.585 3.108-3.108-2.6-1.49a1.05 1.05 0 00-.004-.001z" fill="#FBBC04"/>
    <path d="M20.708 11.067l-2.115-1.215-3.225 3.148 3.222 3.226 2.118-1.213a1.65 1.65 0 000-2.946z" fill="#EA4335"/>
    <path d="M6.4 21.128l10.093-5.836-2.7-2.7L6.4 21.127z" fill="#4285F4"/>
  </svg>
);

export function AppStoreButtons({ variant = 'light', layout = 'row', dataTestPrefix = 'marketing-store' }) {
  const isDark = variant === 'dark';
  const [isIOS] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isIOSDevice();
  });
  const [showIosModal, setShowIosModal] = useState(false);

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 20px',
    borderRadius: 14,
    border: isDark ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(17,17,17,0.16)',
    background: isDark ? 'rgba(255,255,255,0.06)' : '#161127',
    color: isDark ? '#FFFFFF' : '#FFFFFF',
    textDecoration: 'none',
    fontFamily: "'Manrope', sans-serif",
    cursor: 'pointer',
    transition: 'transform 180ms ease, box-shadow 220ms ease, background-color 200ms ease',
    minWidth: 168,
  };

  return (
    <div
      data-testid={`${dataTestPrefix}-buttons`}
      style={{
        display: 'flex',
        flexDirection: layout === 'column' ? 'column' : 'row',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <a
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${dataTestPrefix}-google-play`}
        style={{
          ...baseStyle,
          boxShadow: isDark ? 'none' : '0 10px 24px rgba(22,17,39,0.18)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
      >
        <PlayStoreIcon size={26} />
        <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
          <span style={{ fontSize: 10.5, letterSpacing: 0.6, fontWeight: 500, opacity: 0.7, textTransform: 'uppercase' }}>Get it on</span>
          <span style={{ fontSize: 16, fontWeight: 700 }}>Google Play</span>
        </span>
      </a>

      {isIOS ? (
        <a
          href={SAFARI_APP_URL}
          data-testid={`${dataTestPrefix}-ios-safari`}
          style={{
            ...baseStyle,
            background: isDark ? 'rgba(255,255,255,0.08)' : '#161127',
            color: '#FFFFFF',
            border: isDark ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(17,17,17,0.16)',
            boxShadow: isDark ? 'none' : '0 10px 24px rgba(22,17,39,0.18)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <Globe size={26} strokeWidth={2} />
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
            <span style={{ fontSize: 10.5, letterSpacing: 0.6, fontWeight: 500, opacity: 0.75, textTransform: 'uppercase' }}>Use it now in</span>
            <span style={{ fontSize: 16, fontWeight: 700 }}>Safari</span>
          </span>
        </a>
      ) : (
        <button
          type="button"
          onClick={() => setShowIosModal(true)}
          data-testid={`${dataTestPrefix}-app-store`}
          aria-label="App Store coming soon — explore in Safari"
          style={{
            ...baseStyle,
            background: isDark ? 'rgba(255,255,255,0.06)' : '#161127',
            color: '#FFFFFF',
            border: isDark ? '1px solid rgba(255,255,255,0.22)' : '1px solid rgba(17,17,17,0.16)',
            boxShadow: isDark ? 'none' : '0 10px 24px rgba(22,17,39,0.18)',
            cursor: 'pointer',
            opacity: 1,
            position: 'relative',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          <Apple size={26} fill="currentColor" stroke="currentColor" strokeWidth={0} />
          <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1, textAlign: 'left' }}>
            <span style={{ fontSize: 10, letterSpacing: 0.55, fontWeight: 500, opacity: 0.8, textTransform: 'uppercase' }}>Coming soon to App Store</span>
            <span style={{ fontSize: 16, fontWeight: 700 }}>Explore in Safari</span>
          </span>
        </button>
      )}

      {showIosModal ? (
        <IosComingSoonModal
          onClose={() => setShowIosModal(false)}
          dataTestPrefix={dataTestPrefix}
        />
      ) : null}
    </div>
  );
}

function IosComingSoonModal({ onClose, dataTestPrefix }) {
  return (
    <div
      data-testid={`${dataTestPrefix}-ios-modal`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ios-modal-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(10, 8, 22, 0.62)',
        backdropFilter: 'blur(6px)',
        zIndex: 10600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        fontFamily: "'Manrope', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#FFFFFF',
          color: '#111',
          borderRadius: 22,
          maxWidth: 460,
          width: '100%',
          padding: '24px 22px 22px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.30)',
          position: 'relative',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid={`${dataTestPrefix}-ios-modal-close`}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            border: 'none',
            background: 'rgba(0,0,0,0.05)',
            borderRadius: 999,
            width: 32,
            height: 32,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>

        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: '#161127',
            color: '#FFF',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 14,
          }}
        >
          <Apple size={22} fill="currentColor" stroke="currentColor" strokeWidth={0} />
        </div>

        <h3
          id="ios-modal-title"
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 22,
            margin: '0 0 8px',
            color: '#111',
          }}
        >
          iPhone app — coming soon
        </h3>
        <p style={{ fontSize: 14, color: '#444', lineHeight: 1.55, margin: '0 0 14px' }}>
          We&apos;re putting the finishing touches on the App Store version. In the
          meantime, you can use the <strong>full Allure experience right now</strong> in
          your browser — sign in, accept invites, redeem QR codes, everything.
        </p>
        <p style={{ fontSize: 12.5, color: '#666', lineHeight: 1.5, margin: '0 0 18px' }}>
          For the best experience, open this page in <strong>Safari</strong> on your iPhone
          and tap <em>Share → Add to Home Screen</em> — you&apos;ll get a real app icon and push
          notifications.
        </p>

        <a
          href={SAFARI_APP_URL}
          data-testid={`${dataTestPrefix}-ios-modal-open-web`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            width: '100%',
            padding: '14px 18px',
            borderRadius: 14,
            background: '#161127',
            color: '#FFFFFF',
            textDecoration: 'none',
            fontWeight: 700,
            fontSize: 14.5,
            letterSpacing: 0.2,
            boxShadow: '0 10px 24px rgba(22,17,39,0.20)',
          }}
        >
          <Globe size={18} strokeWidth={2.2} />
          Use Allure in your browser
        </a>

        <button
          type="button"
          onClick={onClose}
          data-testid={`${dataTestPrefix}-ios-modal-notify-dismiss`}
          style={{
            display: 'block',
            margin: '12px auto 0',
            background: 'transparent',
            border: 'none',
            color: '#888',
            fontSize: 12.5,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          I&apos;ll wait for the App Store version
        </button>
      </div>
    </div>
  );
}
